import { useEffect, useRef, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { X, Send } from "lucide-react";
import axios from "axios";
import { WhatsAppIcon } from "./WhatsAppIcon";
import { useWa } from "../../context/SiteData";
import { API } from "../../lib/api";


const GREETING = {
  role: "assistant",
  content:
    "Merhaba! Ben Yalı Asistan. Odalar, hizmetler, konum ve kayıt hakkında sorularınızı yanıtlayabilirim.",
  typed: true,
};

const EASE = [0.22, 1, 0.36, 1];

const WaRedirect = () => {
  const wa = useWa();
  return (
  <motion.a
    initial={{ opacity: 0, y: 10, rotateX: -30, scale: 0.95 }}
    animate={{ opacity: 1, y: 0, rotateX: 0, scale: 1 }}
    transition={{ duration: 0.5, ease: EASE }}
    style={{ transformPerspective: 600 }}
    href={wa}
    target="_blank"
    rel="noopener noreferrer"
    data-testid="chat-whatsapp-redirect"
    className="mt-3 inline-flex items-center gap-2.5 rounded-[2px] bg-ink-900 px-4 py-2.5 text-[11px] font-semibold uppercase tracking-[0.12em] text-cream-50 transition-colors duration-300 hover:bg-ink-600"
  >
    <motion.span
      animate={{ scale: [1, 1.18, 1] }}
      transition={{ repeat: Infinity, duration: 1.6, ease: "easeInOut" }}
      className="inline-flex"
    >
      <WhatsAppIcon size={15} />
    </motion.span>
    WhatsApp'tan Bilgi Al
  </motion.a>
  );
};

const Typewriter = ({ text, onTick, onDone }) => {
  const [shown, setShown] = useState("");

  useEffect(() => {
    let i = 0;
    const t = setInterval(() => {
      i += 1;
      setShown(text.slice(0, i));
      onTick && onTick();
      if (i >= text.length) {
        clearInterval(t);
        onDone && onDone();
      }
    }, 16);
    return () => clearInterval(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [text]);

  return (
    <span>
      {shown}
      <motion.span
        animate={{ opacity: [1, 0, 1] }}
        transition={{ repeat: Infinity, duration: 0.8 }}
        className="ml-0.5 inline-block h-[1em] w-[2px] translate-y-[2px] bg-ink-500"
      />
    </span>
  );
};

export const ChatBot = () => {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([GREETING]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const scrollRef = useRef(null);

  const scrollToEnd = () => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  };

  useEffect(() => {
    scrollToEnd();
  }, [messages, loading, open]);

  const send = async (e) => {
    e.preventDefault();
    const text = input.trim();
    if (!text || loading) return;
    const next = [...messages, { role: "user", content: text, typed: true }];
    setMessages(next);
    setInput("");
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/chat`, {
        messages: next.map(({ role, content }) => ({ role, content })),
      });
      setMessages([...next, { role: "assistant", content: data.reply, typed: false }]);
    } catch {
      setMessages([
        ...next,
        { role: "assistant", content: "Bağlantı kurulamadı. Bize WhatsApp'tan ulaşabilirsiniz. [WHATSAPP]", typed: false },
      ]);
    } finally {
      setLoading(false);
    }
  };

  const markTyped = (index) => {
    setMessages((prev) => prev.map((m, i) => (i === index ? { ...m, typed: true } : m)));
  };

  const renderAssistant = (m, i) => {
    const hasWa = m.content.includes("[WHATSAPP]");
    const clean = m.content.replace("[WHATSAPP]", "").trim();
    if (m.typed) {
      return (
        <>
          <p>{clean}</p>
          {hasWa && <WaRedirect />}
        </>
      );
    }
    return (
      <>
        <p>
          <Typewriter text={clean} onTick={scrollToEnd} onDone={() => markTyped(i)} />
        </p>
        {hasWa && m.typed && <WaRedirect />}
      </>
    );
  };

  return (
    <>
      <div className="fixed bottom-[92px] right-5 z-50 sm:bottom-24 sm:right-6" style={{ perspective: "1400px" }}>
        <AnimatePresence>
          {open && (
            <motion.div
              initial={{ opacity: 0, y: 56, rotateX: -24, rotateY: 6, scale: 0.94 }}
              animate={{ opacity: 1, y: 0, rotateX: 0, rotateY: 0, scale: 1 }}
              exit={{ opacity: 0, y: 40, rotateX: -14, scale: 0.96 }}
              transition={{ duration: 0.55, ease: EASE }}
              style={{ transformOrigin: "bottom right", transformStyle: "preserve-3d" }}
              className="flex w-[min(380px,calc(100vw-2.5rem))] flex-col overflow-hidden border border-line bg-white shadow-[0_30px_80px_-20px_rgba(14,14,14,0.35)]"
              data-testid="chat-window"
            >
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.2, duration: 0.4 }}
                className="flex items-center justify-between bg-ink-900 px-5 py-4"
              >
                <div className="flex items-center gap-3">
                  <motion.span
                    initial={{ scale: 0, rotate: -90 }}
                    animate={{ scale: 1, rotate: 0 }}
                    transition={{ delay: 0.3, duration: 0.5, ease: EASE }}
                    className="flex h-9 w-9 items-center justify-center bg-cream-50"
                  >
                    <img src="/y-mark-black.png" alt="Yalı Yurt" className="h-6 w-6 object-contain" />
                  </motion.span>
                  <div>
                    <p className="text-sm font-bold tracking-tight text-cream-50">Yalı Asistan</p>
                    <p className="mt-0.5 flex items-center gap-1.5 text-[10px] font-medium uppercase tracking-[0.18em] text-cream-50/55">
                      <span className="inline-block h-1.5 w-1.5 animate-pulse rounded-full bg-cream-50/70" />
                      Çevrimiçi
                    </p>
                  </div>
                </div>
                <button
                  onClick={() => setOpen(false)}
                  data-testid="chat-close"
                  aria-label="Kapat"
                  className="text-cream-50/70 transition-colors hover:text-cream-50"
                >
                  <X size={20} />
                </button>
              </motion.div>

              <div ref={scrollRef} className="h-[340px] space-y-3 overflow-y-auto bg-cream-100 p-4 sm:h-[380px]" data-testid="chat-messages">
                {messages.map((m, i) => (
                  <motion.div
                    key={i}
                    initial={{ opacity: 0, y: 14, rotateX: -12 }}
                    animate={{ opacity: 1, y: 0, rotateX: 0 }}
                    transition={{ duration: 0.45, ease: EASE }}
                    style={{ transformPerspective: 700, transformOrigin: m.role === "user" ? "bottom right" : "bottom left" }}
                    data-testid={`chat-message-${m.role}-${i}`}
                    className={`max-w-[85%] px-4 py-3 text-sm leading-relaxed ${
                      m.role === "user"
                        ? "ml-auto bg-ink-900 text-cream-50"
                        : "border border-line bg-white text-ink-800"
                    }`}
                  >
                    {m.role === "assistant" ? renderAssistant(m, i) : m.content}
                  </motion.div>
                ))}
                {loading && (
                  <motion.div
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="flex max-w-[85%] items-center gap-1.5 border border-line bg-white px-4 py-3"
                    data-testid="chat-typing"
                  >
                    {[0, 1, 2].map((d) => (
                      <motion.span
                        key={d}
                        animate={{ opacity: [0.25, 1, 0.25], y: [0, -3, 0] }}
                        transition={{ repeat: Infinity, duration: 1.1, delay: d * 0.18 }}
                        className="h-1.5 w-1.5 rounded-full bg-ink-500"
                      />
                    ))}
                  </motion.div>
                )}
              </div>

              <form onSubmit={send} className="flex items-center gap-2 border-t border-line bg-white p-3">
                <input
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                  placeholder="Sorunuzu yazın..."
                  data-testid="chat-input"
                  className="flex-1 bg-transparent px-2 py-2 text-sm text-ink-800 outline-none placeholder:text-ink-400"
                />
                <motion.button
                  type="submit"
                  disabled={loading}
                  data-testid="chat-send"
                  aria-label="Gönder"
                  whileHover={{ scale: 1.06 }}
                  whileTap={{ scale: 0.92 }}
                  className="flex h-10 w-10 items-center justify-center bg-ink-900 text-cream-50 transition-colors duration-300 hover:bg-ink-600 disabled:opacity-50"
                >
                  <Send size={16} />
                </motion.button>
              </form>

              <div
                data-testid="chat-powered-by"
                className="flex items-center justify-center gap-1.5 border-t border-line bg-cream-50 py-2"
              >
                <span className="text-[9px] font-semibold uppercase tracking-[0.24em] text-ink-400">
                  Geliştirici
                </span>
                <span className="text-[9px] font-bold uppercase tracking-[0.24em] text-ink-800">
                  irvu.io
                </span>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      <div className="fixed bottom-5 right-5 z-50 sm:bottom-6 sm:right-6">
        {!open && (
          <motion.span
            animate={{ scale: [1, 1.55], opacity: [0.5, 0] }}
            transition={{ repeat: Infinity, duration: 2, ease: "easeOut" }}
            className="absolute inset-0 rounded-full border-2 border-ink-900"
          />
        )}
        <motion.button
          onClick={() => setOpen(!open)}
          data-testid="chat-toggle"
          aria-label="Yalı Asistan'ı aç"
          whileHover={{ scale: 1.07, rotate: open ? 0 : -6 }}
          whileTap={{ scale: 0.92 }}
          className="relative flex h-14 w-14 items-center justify-center rounded-full bg-ink-900 text-cream-50 shadow-[0_14px_35px_-8px_rgba(14,14,14,0.5)]"
        >
          <AnimatePresence mode="wait">
            {open ? (
              <motion.span
                key="x"
                initial={{ rotate: -90, opacity: 0 }}
                animate={{ rotate: 0, opacity: 1 }}
                exit={{ rotate: 90, opacity: 0 }}
                transition={{ duration: 0.25 }}
              >
                <X size={22} />
              </motion.span>
            ) : (
              <motion.span
                key="bot"
                initial={{ rotate: 90, opacity: 0 }}
                animate={{ rotate: 0, opacity: 1 }}
                exit={{ rotate: -90, opacity: 0 }}
                transition={{ duration: 0.25 }}
              >
                <img src="/y-mark-white.png" alt="" className="h-6 w-6 object-contain" />
              </motion.span>
            )}
          </AnimatePresence>
        </motion.button>
      </div>
    </>
  );
};
